import React, { useEffect, useMemo, useRef, useState } from "react"; 
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGift,
  faRotateRight,
  faSearch,
  faChevronLeft,
  faChevronRight,
  faArrowRight,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import {
  Card,
  Table,
  Badge,
  Form,
  InputGroup,
  Button,
  Modal,
  Row,
  Col,
} from "@themesberg/react-bootstrap";
import {
  adminEligibleUsersList,
  adminGetBonusCycles,
  adminGetBonusSlabs,
  adminProcessGift,
} from "../../../pages/api";
import { Routes } from "../../../routes";
import { Link, useNavigate } from "react-router-dom";

const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};

const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN");

const badgeVariant = (status) => {
  const s = String(status || "").toLowerCase();
  if (["processed", "sent", "eligible", "active", "ready to process"].includes(s)) return "success";
  if (["pending", "draft"].includes(s)) return "warning";
  if (["failed", "inactive", "not eligible", "already claimed", "archived"].includes(s)) return "danger";
  return "secondary";
};

const getPageItems = (currentPage, totalPages) => {
  if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (currentPage <= 2) return [1, 2, 3, "...", totalPages];
  if (currentPage >= totalPages - 1) return [1, "...", totalPages - 2, totalPages - 1, totalPages];
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
};

const getShowingRange = (pagination, currentRowsCount = 0) => {
  const total = Number(pagination.total || 0);
  const current = Number(pagination.current_page || 1);
  const perPage = Number(pagination.per_page || 10);

  if (total === 0 || currentRowsCount === 0) return { start: 0, end: 0 };

  let start = (current - 1) * perPage + 1;
  let end = Math.min(start + currentRowsCount - 1, total);

  if (start > total) {
    start = total;
    end = total;
  }

  if (end < start) {
    end = start;
  }

  return { start, end };
};

const mapCycleOption = (item) => ({
  id: item?.id ?? Date.now(),
  cycle_name: item?.cycle_name || "",
  cycle_month: item?.cycle_month || "",
  status: item?.status || "",
});

const mapSlabOption = (item) => ({
  id: item?.id ?? Date.now(),
  slab_name: item?.slab_name || "",
  threshold: item?.threshold ?? "",
});

const mapEligibleUser = (item) => ({
  id: item?.user_id ?? Date.now(),
  user_id: item?.user_id ?? "N/A",
  user_name: item?.user_name || "N/A",
  email: item?.email || "",
  cycle_id: item?.cycle_id ?? "",
  cycle_name: item?.cycle_name || "",
  cycle_month: item?.cycle_month || "",
  followers: item?.followers ?? 0,
  highest_slab: item?.highest_slab || "N/A",
  new_eligible_slabs: Array.isArray(item?.new_eligible_slabs) ? item.new_eligible_slabs : [],
  already_claimed: Array.isArray(item?.already_claimed) ? item.already_claimed : [],
  status: item?.status || "Eligible",
  processing_status: item?.processing_status || item?.status || "Pending",
  message_status: item?.message_status || "Pending",
  amount: Array.isArray(item?.new_eligible_slabs)
    ? item.new_eligible_slabs.reduce(
        (sum, slab) => sum + Number(slab?.amount || 0),
        0
      )
    : 0,
});

const formatSlabLabel = (slab) => {
  if (!slab) return "N/A";
  if (typeof slab === "string") return slab;
  return slab?.slab_name || slab?.name || "N/A";
};

const formatCurrency = (value) => {
  const num = Number(value ?? 0);
  if (Number.isNaN(num)) return "0.00";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export default function AdminEligibleUsers() {
  const navigate = useNavigate();
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [processingGift, setProcessingGift] = useState(false);

  const [cycleFilter, setCycleFilter] = useState("");
  const [slabFilter, setSlabFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [page, setPage] = useState(1);
  const [selectedCycleToProcess, setSelectedCycleToProcess] = useState("");

  const [users, setUsers] = useState([]);
  const [cycleOptions, setCycleOptions] = useState([]);
  const [slabOptions, setSlabOptions] = useState([]);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const initialLoadRef = useRef(true);
  const searchDebounceRef = useRef(null);

  const clearSearchDebounce = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
  };

  const fetchCycleOptions = async () => {
    try {
      const response = await adminGetBonusCycles({ pagination: false });
      const responseData = response?.data || {};
      const apiData = responseData?.data || {};
      const apiCycles = Array.isArray(apiData?.cycles) ? apiData.cycles : [];
      setCycleOptions(apiCycles.map(mapCycleOption));
    } catch (error) {
      console.error("Failed to fetch cycle options:", error);
      toast.error(error?.response?.data?.message || "Unable to fetch cycle options.");
      setCycleOptions([]);
    }
  };

  const fetchSlabOptions = async () => {
    try {
      const response = await adminGetBonusSlabs({ pagination: false });
      const responseData = response?.data || {};
      const apiData = responseData?.data || {};
      const apiSlabs = Array.isArray(apiData?.slabs) ? apiData.slabs : [];
      setSlabOptions(apiSlabs.map(mapSlabOption));
    } catch (error) {
      console.error("Failed to fetch slab options:", error);
      toast.error(error?.response?.data?.message || "Unable to fetch slab options.");
      setSlabOptions([]);
    }
  };

  const fetchEligibleUsers = async (pageNo = 1, perPage = 10, filters = {}) => {
    try {
      setLoadingUsers(true);

      const params = {
        page: pageNo,
        per_page: perPage,
      };

      if (filters.cycleId) params.cycle_id = filters.cycleId;
      if (filters.slabId) params.slab_id = filters.slabId;

      const trimmedSearch = String(filters.search || "").trim();
      if (trimmedSearch) {
        params.search = trimmedSearch;
      }

      const response = await adminEligibleUsersList(params);
      const responseData = response?.data || {};
      const apiData = responseData?.data || {};
      const apiUsers = Array.isArray(apiData?.users) ? apiData.users : [];
      const apiPagination = apiData?.pagination || {};

      setUsers(apiUsers.map(mapEligibleUser));
      setPagination({
        current_page: apiPagination.current_page ?? 1,
        last_page: apiPagination.last_page ?? 1,
        per_page: apiPagination.per_page ?? 10,
        total: apiPagination.total ?? apiUsers.length,
      });
    } catch (error) {
      console.error("Failed to fetch eligible users:", error);
      toast.error(error?.response?.data?.message || "Unable to fetch eligible users.");
      setUsers([]);
      setPagination({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    (async () => {
      setLoadingOptions(true);
      try {
        await Promise.all([fetchCycleOptions(), fetchSlabOptions()]);
      } finally {
        setLoadingOptions(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const filters = {
      cycleId: cycleFilter,
      slabId: slabFilter,
      search,
    };

    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      fetchEligibleUsers(1, 10, filters);
      return undefined;
    }

    clearSearchDebounce();
    const delay = String(search || "").trim() ? 300 : 0;

    searchDebounceRef.current = setTimeout(() => {
      fetchEligibleUsers(page, pagination.per_page || 10, filters);
    }, delay);

    return () => {
      clearSearchDebounce();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, cycleFilter, slabFilter, search]);

  const handleCycleChange = (value) => {
    setCycleFilter(value);
    setPage(1);
    setSelectedIds([]);
  };

  const handleSlabChange = (value) => {
    setSlabFilter(value);
    setPage(1);
    setSelectedIds([]);
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
    setSelectedIds([]);
  };

  const handleRefresh = async () => {
    setPage(1);
    setSelectedIds([]);
    clearSearchDebounce();

    await Promise.all([
      fetchCycleOptions(),
      fetchSlabOptions(),
      fetchEligibleUsers(1, pagination.per_page || 10, {
        cycleId: cycleFilter,
        slabId: slabFilter,
        search,
      }),
    ]);
  };

  const handleToggleSelectAll = (checked) => {
    if (checked) {
      const pageIds = users
        .filter((item) => item.processing_status === "Pending" || item.processing_status === "Processed")
        .map((item) => item.id);
      setSelectedIds(pageIds);
      return;
    }

    setSelectedIds([]);
  };

  const handleRowSelect = (id, checked) => {
    setSelectedIds((prev) =>
      checked ? Array.from(new Set([...prev, id])) : prev.filter((itemId) => itemId !== id)
    );
  };

  const selectedRows = useMemo(
    () => users.filter((item) => selectedIds.includes(item.id)),
    [users, selectedIds]
  );

  const processableRows = selectedRows.filter(
    (item) => item.new_eligible_slabs.length > 0 && item.processing_status === "Pending"
  );

  const handleProcessGift = () => {
    if (selectedRows.length === 0) {
      toast.error("Please select one or more eligible users.");
      return;
    }

    const selectedCycleIds = Array.from(new Set(selectedRows.map((item) => item.cycle_id).filter(Boolean)));

    if (selectedCycleIds.length === 0) {
      toast.error("Unable to determine cycle for selected users.");
      return;
    }

    if (selectedCycleIds.length > 1) {
      toast.error("Please select users from the same cycle.");
      return;
    }

    if (processableRows.length === 0) {
      toast.error("Selected user has no new eligible slab.");
      return;
    }

    setSelectedCycleToProcess(selectedCycleIds[0]);
    setRemarks("");
    setShowConfirm(true);
  };

  const confirmProcessing = async () => {
    const effectiveCycleId = selectedCycleToProcess || selectedRows?.[0]?.cycle_id || "";

    if (!effectiveCycleId) {
      toast.error("Unable to determine cycle for selected users.");
      return;
    }

    if (processableRows.length === 0) {
      toast.error("No eligible pending gifts selected.");
      setShowConfirm(false);
      return;
    }

    try {
      setProcessingGift(true);

      const payload = {
        cycle_id: effectiveCycleId,
        user_ids: processableRows.map((item) => item.id),
      };

      const trimmedRemarks = String(remarks || "").trim();
      if (trimmedRemarks) {
        payload.remarks = trimmedRemarks;
      }

      const response = await adminProcessGift(payload);
      toast.success(response?.data?.message || "Gift processed successfully.");

      setSelectedIds([]);
      setRemarks("");
      setSelectedCycleToProcess("");
      setShowConfirm(false);

      await fetchEligibleUsers(page, pagination.per_page || 10, {
        cycleId: cycleFilter,
        slabId: slabFilter,
        search,
      });
    } catch (error) {
      console.error("Failed to process gift:", error);
      toast.error(error?.response?.data?.message || "Unable to process gift.");
    } finally {
      setProcessingGift(false);
    }
  };

  const { start, end } = getShowingRange(pagination, users.length);
  const pageItems = getPageItems(pagination.current_page || 1, pagination.last_page || 1);

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4 gap-2">
        <div>
          <h2 className="h4 mb-1">Eligible User List</h2>
          {/* <div className="text-muted">
            Filter Users, inspect slab eligibility, and process gifts with pagination.
          </div> */}
        </div>

        <div className="d-flex justify-content-end ms-auto">
          <Button
            type="button"
            variant="outline-primary"
            onClick={() => navigate(-1)}
            className="admin-back-btn"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </Button>
        </div>
      </div>

      <Card className="border-0 shadow-sm rounded-4 mb-4">
        <Card.Body className="p-4">
          <Row className="g-3 align-items-end">
            <Col xs={12} md={4}>
              <Form.Label className="fw-semibold">Filter by Cycle</Form.Label>
              <Form.Select
                value={cycleFilter}
                onChange={(e) => handleCycleChange(e.target.value)}
                disabled={loadingOptions}
              >
                <option value="">All Cycles</option>
                {cycleOptions.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.cycle_name}
                    {item.cycle_month ? ` (${item.cycle_month})` : ""}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12} md={4}>
              <Form.Label className="fw-semibold">Filter by Slab</Form.Label>
              <Form.Select
                value={slabFilter}
                onChange={(e) => handleSlabChange(e.target.value)}
                disabled={loadingOptions}
              >
                <option value="">All Slabs</option>
                {slabOptions.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.slab_name}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12} md={4} className="ms-md-auto">
              <Form.Label className="fw-semibold">Search</Form.Label>
              <InputGroup className="bonus-search-group">
                <InputGroup.Text className="bg-white">
                  <FontAwesomeIcon icon={faSearch} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search"
                />
              </InputGroup>
            </Col>
          </Row>

          <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-2">
            <div className="text-muted small">Selected {selectedIds.length} Users</div>
            <div className="d-flex gap-2 flex-wrap ms-auto">
              {/* <Button variant="outline-primary" onClick={handleRefresh}>
                <FontAwesomeIcon icon={faRotateRight} className="me-2" />
                Refresh
              </Button> */}
              <Button style={ACTIVE_BUTTON_STYLE} onClick={handleProcessGift}>
                <FontAwesomeIcon icon={faGift} className="me-2" />
                Process Gift
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm rounded-4">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div>
              <h5 className="mb-1">Eligible Users</h5>
              {/* <div className="text-muted small">
                New eligible slabs are loaded from the backend.
              </div> */}
            </div>
          </div>

          <div className="table-responsive">
            <Table hover className="align-middle mb-0 bonus-table">
              <thead>
                <tr>
                  <th style={{ width: 48 }}>
                    <Form.Check
                      type="checkbox"
                      checked={users.length > 0 && users.every((item) => selectedIds.includes(item.id))}
                      onChange={(e) => handleToggleSelectAll(e.target.checked)}
                    />
                  </th>
                  {/* <th>User ID</th> */}
                  <th>Name</th>
                  <th>Followers in Cycle</th>
                  <th>Highest Slab Achieved</th>
                  <th>New Eligible Slabs</th>
                  <th className="text-center">Amount</th>
                  <th className="text-end">Already Claimed Slabs</th>
                  {/* <th>Processing Status</th>
                  <th>Message Status</th> */}
                </tr>
              </thead>
              <tbody>
                {loadingUsers ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-5">
                      Loading eligible Users...
                    </td>
                  </tr>
                ) : users.length > 0 ? (
                  users.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedIds.includes(item.id)}
                          onChange={(e) => handleRowSelect(item.id, e.target.checked)}
                          disabled={item.processing_status === "Not Eligible"}
                        />
                      </td>
                      {/* <td className="fw-semibold">{item.user_id}</td> */}
                      <td>{item.user_name}</td>
                      <td>{formatNumber(item.followers)}</td>
                      <td>{item.highest_slab}</td>
                      <td>
                        {item.new_eligible_slabs.length
                          ? item.new_eligible_slabs.map(formatSlabLabel).join(", ")
                          : "N/A"}
                      </td>
                      <td className="text-center">{formatCurrency(item.amount)}</td>
                      <td className="text-end">
                        {item.already_claimed.length
                          ? item.already_claimed.map(formatSlabLabel).join(", ")
                          : "N/A"}
                      </td>
                      {/* <td>
                        <Badge bg={badgeVariant(item.processing_status)} className="rounded-pill px-3 py-2">
                          {item.processing_status}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={badgeVariant(item.message_status)} className="rounded-pill px-3 py-2">
                          {item.message_status}
                        </Badge>
                      </td> */}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-5">
                      No records found for selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <div className="text-muted small">
              Showing {start} to {end} of {pagination.total} entries
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline-primary"
                disabled={pagination.current_page <= 1 || loadingUsers}
                onClick={() => {
                  clearSearchDebounce();
                  fetchEligibleUsers(Math.max(1, pagination.current_page - 1), pagination.per_page, {
                    cycleId: cycleFilter,
                    slabId: slabFilter,
                    search,
                  });
                }}
                className="course-page-btn"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </Button>

              {pageItems.map((item, index) =>
                item === "..." ? (
                  <span key={`dots-${index}`} className="text-muted px-1">
                    ...
                  </span>
                ) : (
                  <Button
                    key={item}
                    type="button"
                    variant="outline-primary"
                    disabled={loadingUsers}
                    onClick={() => {
                      clearSearchDebounce();
                      fetchEligibleUsers(item, pagination.per_page, {
                        cycleId: cycleFilter,
                        slabId: slabFilter,
                        search,
                      });
                    }}
                    className={`course-page-btn ${item === pagination.current_page ? "course-page-btn-active" : ""}`}
                  >
                    {item}
                  </Button>
                )
              )}

              <Button
                type="button"
                variant="outline-primary"
                disabled={pagination.current_page >= pagination.last_page || loadingUsers}
                onClick={() => {
                  clearSearchDebounce();
                  fetchEligibleUsers(Math.min(pagination.last_page, pagination.current_page + 1), pagination.per_page, {
                    cycleId: cycleFilter,
                    slabId: slabFilter,
                    search,
                  });
                }}
                className="course-page-btn"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Process Gift Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">Are you sure you want to process gift(s) for selected eligible Users?</p>
          <Form.Group className="mb-0">
            <Form.Label className="fw-semibold">Remarks</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Optional admin remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              style={{ boxShadow: "none" }}
            />
          </Form.Group>
          <div className="mt-3 small text-muted">
            Processable selected rows: <strong>{processableRows.length}</strong>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowConfirm(false)} disabled={processingGift}>
            Cancel
          </Button>
          <Button style={ACTIVE_BUTTON_STYLE} onClick={confirmProcessing} disabled={processingGift}>
            {processingGift ? "Processing..." : "Yes, Process"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}